import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TextWithOverflowTooltip from '../TextWithOverflowTooltip';
import { buildItemCardLines, ItemCardData } from './itemCardFormatters';
import { UsageSummaryPill } from './UsageSummaryPill';

export type ProductDetailsItem = ItemCardData & {
  thumbnailUrl?: string | null;
  name?: string | null;
  sku?: string | null;
  gtin?: string | null;
};

export const ProductDetailsCard = ({
  item,
  currencyCode,
  categoryLabel,
  parText,
  showMessageWhenNoCost,
}: {
  item: ProductDetailsItem;
  currencyCode?: string;
  categoryLabel?: string | null;
  parText?: string | null;
  showMessageWhenNoCost?: boolean;
}) => {
  const theme = useTheme();
  const { t } = useTranslation('orderForm');
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const lines = buildItemCardLines(item, currencyCode, {
    order: t('itemCardOrderLabel'),
    onOrder: t('itemCardOnOrderLabel'),
    onHand: t('itemCardOnHandLabel'),
  }, { showMessageWhenNoCost });

  return (
    <Grid container
      alignItems={'stretch'}
      spacing={1}
      wrap="nowrap">
      <Grid item
        xs="auto">
        <Box
          sx={{
            width: 104,
            height: '100%',
            minHeight: 75,
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme.palette.custom.gray[300],
            backgroundColor: item.thumbnailUrl ? theme.palette.common.white : theme.palette.custom.gray[200],
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            backgroundImage: item.thumbnailUrl ? `url(${item.thumbnailUrl})` : 'none',
          }}
        />
      </Grid>
      <Grid item
        sx={{ minWidth: 0, paddingLeft: '10px !important', flex: 1 }}>
        <Grid container
          spacing={2}
          alignItems="center">
          <Grid item
            xs={isLargeScreen ? 6 : 12}>
            <Box sx={{ fontWeight: 600 }}>
              <TextWithOverflowTooltip text={item.name ?? ''} />
            </Box>
            <TextWithOverflowTooltip text={item.sku ?? ''} />
            {categoryLabel && <TextWithOverflowTooltip text={categoryLabel} />}
            {parText && <TextWithOverflowTooltip text={parText} />}
            <Box sx={{ pt: 0.5 }}>
              <UsageSummaryPill item={item} />
            </Box>
          </Grid>
          <Grid item
            xs={isLargeScreen ? 6 : 12}>
            {lines.map((line) => (
              <TextWithOverflowTooltip key={line}
                text={line} />
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ProductDetailsCard;
